import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import InfoCard from './components/InfoCard';
import MetricsCard from './components/MetricsCard';
import ActivityChart from './components/ActivityChart';
import RecentTransactions from './components/RecentTransactions';
import { merchantsService, transactionsService } from '../../services/supabaseService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const MerchantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [merchant, setMerchant] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: merchantData } = await merchantsService?.getById(id);
      const { data: transactionsData } = await transactionsService?.getAll({ merchantId: id, limit: 10 });

      if (merchantData) {
        setMerchant(merchantData);
      }

      if (transactionsData) {
        setTransactions(transactionsData);
        
        // Generate chart data (last 7 days)
        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date?.setDate(date?.getDate() - i);
          const dateStr = date?.toISOString()?.split('T')?.[0];
          
          const dayTransactions = transactionsData?.filter(t => 
            t?.createdAt?.startsWith(dateStr)
          );
          
          last7Days?.push({
            date: date?.toLocaleDateString('id-ID', { day: '2-digit', month: 'short' }),
            transactions: dayTransactions?.length || 0,
            revenue: dayTransactions?.reduce((sum, t) => sum + (parseFloat(t?.amount) || 0), 0)
          });
        }
        setChartData(last7Days);
      }
    } catch (error) {
      console.error('Error fetching merchant details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await merchantsService?.update(id, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Error updating merchant status:', error);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Merchant Details | PaymentNexus</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div className="h-64 bg-muted rounded-xl"></div>
                  <div className="h-96 bg-muted rounded-xl"></div>
                </div>
                <div className="space-y-6">
                  <div className="h-64 bg-muted rounded-xl"></div>
                  <div className="h-48 bg-muted rounded-xl"></div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  if (!merchant) {
    return (
      <>
        <Helmet>
          <title>Merchant Not Found | PaymentNexus</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={64} className="mx-auto text-muted-foreground mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Merchant Not Found</h2>
              <p className="text-muted-foreground mb-6">The merchant you're looking for doesn't exist</p>
              <Button onClick={() => navigate('/merchants')}>Back to Merchants</Button>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{merchant?.merchantName} - Details | PaymentNexus</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/merchants')}
                className="flex items-center gap-2"
              >
                <Icon name="ArrowLeft" size={16} />
                Back
              </Button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">{merchant?.merchantName}</h1>
                <p className="text-muted-foreground">Merchant ID: {merchant?.merchantId}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/merchants/${id}/transactions`)}
                className="flex items-center gap-2"
              >
                <Icon name="Receipt" size={16} />
                View Transactions
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/merchants/${id}/onboarding`)}
                className="flex items-center gap-2"
              >
                <Icon name="ClipboardCheck" size={16} />
                Onboarding Status
              </Button>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <MetricsCard
                  title="Total Transactions"
                  value={(merchant?.totalTransactions || 0)?.toLocaleString()}
                  icon="Activity"
                  iconColor="text-primary"
                  iconBg="bg-primary/10"
                />
                <MetricsCard
                  title="Total Revenue"
                  value={`Rp ${(merchant?.totalRevenue || 0)?.toLocaleString('id-ID')}`}
                  icon="DollarSign"
                  iconColor="text-success"
                  iconBg="bg-success/10"
                />
                <MetricsCard
                  title="Success Rate"
                  value={`${(merchant?.successRate || 0)?.toFixed(1)}%`}
                  icon="TrendingUp"
                  iconColor="text-warning"
                  iconBg="bg-warning/10"
                />
              </div>

              {/* Activity Chart */}
              <ActivityChart data={chartData} />

              {/* Recent Transactions */}
              <RecentTransactions transactions={transactions} merchantId={id} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Merchant Info */}
              <InfoCard merchant={merchant} onStatusChange={handleStatusChange} />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default MerchantDetails;